<template>
	<b-container fluid>
		<b-row class="mt-3" align-content="center" no-gutters>
			<!-- Admin options -->
			<b-col v-if="isAdmin" lg>
				<!-- Date selection exit/confirm -->
				<b-button-group v-if="dateSelectionMode" class='d-block text-center'>
					<b-button variant="danger" @click="exitDateSelectionMode()">
						<b-icon icon="x-square" scale="1"></b-icon>
					</b-button>
					<b-button 
						v-if="dateSelection.length" 
						variant="success" 
						@click="adminConfirmAddNewClasses()"
					>
						<b-icon icon="check-square" scale="1"></b-icon>
					</b-button>
				</b-button-group>

				<!-- Manage patient done -->
				<b-button
					v-else-if="managePatient.userID"
					variant="danger"
					class='d-block text-center mx-auto'
					@click="exitManagePatient()"
				>
					<b-icon icon='x'></b-icon> {{ managePatient.firstName + ' ' + managePatient.lastName }}
				</b-button>

				<!-- Admin option buttons -->
				<b-button-group v-else  class='d-block text-center'>
					<b-button variant="success" @click="showAddNewClassesModal()">Add Classes</b-button>
					<b-button variant="primary" v-b-modal="'addPatientToClassesModal'">Manage Patient</b-button>
				</b-button-group>
			</b-col>
			<b-col v-else>
				<div id="legends" class="mx-auto">
					<span class="available">Available</span>
					<span class="signedUp">
						<strong>Signed Up</strong>
					</span>
					<span class="full">
						<strong>Full</strong>
					</span>
				</div>
			</b-col>
			<b-col id="monthYearCol" lg>
				<div class="d-block mx-auto text-center">
					<b-button id="prevMonth" @click="prevMonth()" variant="light" pill>
						<b-icon icon="chevron-double-left"></b-icon>
					</b-button>

					<h4 id="monthYear">{{ this.$months[month] + " " + year }}</h4>

					<b-button
						v-if="isAdmin || unrestrictedNextMonth"
						id="nextMonth"
						@click="nextMonth()"
						variant="light"
						pill
					>
						<b-icon icon="chevron-double-right"></b-icon>
					</b-button>
				</div>
			</b-col>
			<b-col lg>
				<div
					class="text-danger my-1 text-center"
				>Class attendance must be cancelled by 4pm on the previous day</div>
			</b-col>
		</b-row>

		<b-modal id="addNewClassesModal" size="lg" centered>
			<b-container fluid><b-row class="text-center my-2">
					<b-col lg>
						<strong>Roster count</strong>
						<div class="my-2">{{ newClass.roster.length }}</div>
					</b-col>
					<b-col>
						<strong>Time</strong>
						<b-form-timepicker
							v-model="newClass.time"
							placeholder="Pick a time..."
							class="my-2 text-center"
							minutes-step="15"
						></b-form-timepicker>
					</b-col>
					<b-col>
						<strong class="d-block my-1 text-center">Instructor</strong>
						<b-form-select
							v-model="newClass.instructorID"
							:options="instructorOptions"
							class="my-2 text-center"
						></b-form-select>
					</b-col>
				</b-row>

				<b-row class="my-2">
					<b-col class="text-center" lg>
						<strong class="d-block my-1">Roster</strong>
						<b-button
							v-for="attendee in newClass.roster"
							:key="attendee.userID"
							variant="light"
							class="attendee w-75 mx-auto d-block my-2"
							@click="adminRemoveUserFromNewClasses(attendee)"
						>
							{{ attendee.firstName + " " + attendee.lastName }}
							<b-icon icon="x-square" class="my-1"></b-icon>
						</b-button>
					</b-col>
				</b-row>
			</b-container>

			<template v-slot:modal-footer>
				<b-container fluid>
					<b-button
						variant="primary"
						class="float-right mx-2"
						@click="enterDateSelectionMode()"
					>Select Dates</b-button>

					<b-button
						variant="success"
						class="float-right mx-2"
						:disabled="newClass.roster.length === max"
						@click="showPatientSearchModal()"
					>Add To Roster</b-button>

					<b-button
						variant="secondary"
						class="float-right mx-2"
						@click="$root.$emit('bv::hide::modal', 'addNewClassesModal')"
					>Back</b-button>
				</b-container>
			</template>
		</b-modal>

		<b-modal
			id="patientSearchModal"
			title="Patient Search"
			ok-only
			ok-title="Back"
			ok-variant="secondary"
		>
			<b-container fluid>
				<b-row>
					<b-col>
						<b-form-input
							id="searchInput"
							placeholder="eg. John Doe"
							class="text-center w-75 mx-auto"
							v-model="searchQuery"
							@update="adminPatientSearch('addClasses')"
						></b-form-input>
					</b-col>
				</b-row>
				<b-row>
					<b-col>
						<b-button
							v-for="patient in searchResults"
							:key="patient.userID"
							@click="adminAddUserToNewClasses(patient)"
							variant="light"
							class="searchResult"
						>{{ patient.firstName + " " + patient.lastName }}</b-button>
					</b-col>
				</b-row>
			</b-container>
		</b-modal>

		<b-modal
			id="addPatientToClassesModal"
			title="Patient Search"
			ok-only
			ok-title="Back"
			ok-variant="secondary"
		>
			<b-container fluid>
				<b-row>
					<b-col>
						<b-form-input
							id="searchInput"
							placeholder="eg. John Doe"
							class="text-center w-75 mx-auto"
							v-model="searchQuery"
							@update="adminPatientSearch('managePatient')"
						></b-form-input>
					</b-col>
				</b-row>
				<b-row>
					<b-col>
						<b-button
							v-for="patient in searchResults"
							:key="patient.userID"
							@click="selectPatientToManage(patient)"
							variant="light"
							class="searchResult"
						>{{ patient.firstName + " " + patient.lastName }}</b-button>
					</b-col>
				</b-row>
			</b-container>
		</b-modal>

		<Month :month="month" :year="year" />
	</b-container>
</template>

<script>
// @ is an alias to /src
import EventBus from "@/eventBus"
import Month from "@/components/MedEx/Month.vue"

// 6 -> 06
function padWithZero(day) {
	if (day.length === 1) {
		return "0" + day
	} else {
		return day
	}
}

export default {
	name: "MedEx",
	data() {
		return {
			month: new Date().getMonth(), // 0-11
			year: new Date().getFullYear(), // YYYY
			newClass: {
				date: "",
				time: "",
				isSignedUp: false,
				instructor: {
					id: 0,
					firstName: "",
					lastName: "",
					creds: "",
				},
				roster: [],
			},
			searchQuery: "",
			searchResults: [],
			instructorOptions: [],
		}
	},
	components: {
		Month,
	},
	computed: {
		isAdmin() {
			return this.$store.getters.isAdmin
		},
		dateSelectionMode() {
			return this.$store.state.dateSelectionMode
		},
		dateSelection() {
			return this.$store.getters.dateSelection
		},
		timeString() {
			try {
				if (parseInt(this.newClass.time.substr(0, 2)) > 12) {
					// Get hour substring from full time string
					// convert to integer and deduct 12
					// pad with zero if necessary
					// append PM
					return (
						padWithZero(
							(parseInt(this.newClass.time.substr(0, 2)) - 12).toString()
						) +
						this.newClass.time.substr(2, 3) +
						"PM"
					)
				} else {
					return this.newClass.time.substr(0, 5) + "AM"
				}
			} catch {
				return ""
			}
		},
		displayText() {
			try {
				return this.timeString + " - " + this.newClass.template.title
			} catch {
				return ""
			}
		},
		isFull() {
			if (this.newClass.roster.length === this.max) {
				return true
			} else {
				return false
			}
		},
		unrestrictedNextMonth() {
			return this.month < new Date().getMonth() + 2 ? true : false
		},
		managePatient() {
			return this.$store.state.managePatient
		},
		max() {
			return this.$max
		}
	},
	methods: {
		// Go to prev month
		prevMonth() {
			// If month == 0 go to last month of prev year, else subtract 1
			if (this.month == 0) {
				this.month = 11
				--this.year
			} else {
				--this.month
			}
		},
		// Go to next month
		nextMonth() {
			// If month == 11 go to first month of next year, else add 1
			if (this.month == 11) {
				this.month = 0
				++this.year
			} else {
				++this.month
			}
		},
		checkLogin: function() {
			return new Promise((resolve, reject) => {
				if (this.$store.state.token) {
					fetch(this.$apiURL + "/checkLogin", {
						method: "GET",
						headers: new Headers({
							Authorization: this.$store.state.token,
							"Content-Type": "application/json",
						}),
					}).then(res => {
						if (res.ok) {
							// user is logged in
							res.json().then(user => {
								this.$store.state.user.userID = user.userID
								this.$store.state.user.admin = user.admin
								resolve()
							})
						} else {
							this.$cookies.remove("token")
							this.$cookies.remove("userID")
							this.$cookies.remove("admin")
							this.$router.push("/login.html")
							reject()
						}
					})
				} else {
					this.$cookies.remove("token")
					this.$cookies.remove("userID")
					this.$cookies.remove("admin")
					this.$router.push("/Login")
					reject()
				}
			})
		},
		fetchInstructors: function() {
			return new Promise((resolve, reject) => {
				fetch(this.$apiURL + "/getInstructors", {
					method: "GET",
					headers: new Headers({
						Authorization: this.$store.state.token,
						"Content-Type": "application/json",
					}),
				}).then(res => {
					if (res.ok) {
						res.json().then(instructors => {
							// Reset to initial placeholder option, then append data
							this.instructorOptions = [
								{
									value: 0,
									text: "Select an instructor...",
								},
							]
							instructors.forEach(instructor => {
								this.instructorOptions.push({
									value: instructor.id,
									text: instructor.firstName + ' ' + instructor.lastName + ' ' + instructor.creds
								})
							})
							resolve()
						})
					} else {
						console.error("Error fetching instructors")
						reject()
					}
				})
			})
		},
		showAddNewClassesModal: async function() {
			await this.fetchInstructors()
			this.$root.$emit("bv::show::modal", "addNewClassesModal")
		},
		showPatientSearchModal: function() {
			this.searchQuery = ""
			this.searchResults = []
			this.$root.$emit("bv::show::modal", "patientSearchModal")
		},
		adminPatientSearch: function(context) {
			return new Promise((resolve, reject) => {
				if (this.searchQuery.length < 3) {
					// Dont query or show any results if query string is less than 3 chars
					this.searchResults = []
					resolve()
				} else {
					fetch(this.$apiURL + "/adminPatientSearchQuery", {
						method: "POST",
						headers: new Headers({
							Authorization: this.$store.state.token,
							"Content-Type": "application/json",
						}),
						body: JSON.stringify({
							query: this.searchQuery,
						}),
					}).then(res => {
						if (res.ok) {
							res.json().then(results => {
								this.searchResults = results.filter(result => {
									if (context === 'addClasses') {
										return !this.newClass.roster.map(attendee => attendee.userID).includes(result.userID)
									}
									else if (context === 'managePatient') {
										return result.userID != this.$store.state.user.userID
									}
								})
								resolve()
							})
						} else {
							// Server error
							alert('Error querying server. Try again.')
							reject()
						}
					})
				}
			})
		},
		adminAddUserToNewClasses: function(patient) {
			this.newClass.roster.push(patient)
			this.$root.$emit("bv::hide::modal", "patientSearchModal")
		},
		adminRemoveUserFromNewClasses: function(attendee) {
			const index = this.newClass.roster.indexOf(attendee)
			if (index > -1) {
				this.newClass.roster.splice(index, 1)
			}
		},
		enterDateSelectionMode: function() {
			// Validate new class config
			if (this.newClass.instructorID === 0) {
				alert("You must select an instructor")
			} else if (this.newClass.time === "") {
				alert("You must pick a time")
			} else {
				// Enter date selection mode
				this.$store.dispatch("enterDateSelectionMode").then(() => {
					this.$root.$emit("bv::hide::modal", "addNewClassesModal")
				})
			}
		},
		exitDateSelectionMode: async function() {
			await this.$store.dispatch("exitDateSelectionMode")
		},
		adminConfirmAddNewClasses: function() {
			new Promise((resolve, reject) => {
				fetch(this.$apiURL + "/adminAddClasses", {
					method: "POST",
					headers: new Headers({
						Authorization: this.$store.state.token,
						"Content-Type": "application/json",
					}),
					body: JSON.stringify({
						dates: this.dateSelection,
						time: this.newClass.time,
						instructorID: this.newClass.instructorID,
						roster: this.newClass.roster.map(patient => patient.userID),
					}),
				}).then(res => {
					if (res.ok) {
						this.$store
							.dispatch("exitDateSelectionMode")
							.then(async () => {
								// Reset date selection and newClass config
								await this.$store.dispatch("clearDateSelection")
								this.newClass = {
									date: "",
									time: "",
									isSignedUp: false,
									instructorID: 0,
									roster: [],
								}
								alert("Successfully added classes")
								// Refresh month!!!
								EventBus.$emit("refreshDays")
								resolve()
							})
					} else {
						alert("Something went wrong... Try again.")
						reject(res)
					}
				})
			})
		},
		selectPatientToManage: function (patient) {
			this.$store.dispatch('selectPatient', patient)
				.then(async () => {
					this.$root.$emit("bv::hide::modal", "addPatientToClassesModal")
					this.searchQuery = ""
					this.searchResults = []
				})
		},
		exitManagePatient: async function () {
			await this.$store.dispatch("selectPatient", 0)
		},
	},
	async created() {
		await this.checkLogin()
		await this.$store.dispatch('exitDateSelectionMode')
	},
}
</script>

<style scoped>
#legends {
	display: inline-block;
}

#legends span {
	display: inline-block;
	width: 120px;
	border: 1px solid black;
	margin: 0 auto;
	padding: 5px;
	text-align: center;
}

#legends .available {
	background-color: #f8f9fa;
	color: black;
}

#legends .signedUp {
	background-color: #007bff;
	color: white;
}

#legends .full {
	background-color: #dc3545;
	color: white;
}

#monthYear {
	display: inline-block;
	text-align: center;
	margin: 0 10px;
	min-width: 185px;
}

#prevMonth,
#nextMonth {
	display: inline-block;
	margin: 0 auto;
}

#monthYearCol {
	min-width: 250px;
}

.searchResult {
	display: block;
	width: 80%;
	margin: 15px auto;
	border: 1px solid grey;
}

.bi-x-square {
	float: right;
}

@media screen and (max-width: 992px) {
	#monthYearCol {
		margin-top: 20px;
	}

	#legends {
		display: block;
		text-align: center;
	}
}
</style>
